/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, RefObject, useEffect } from "react"
import { Input as TInput } from "antd"
import { Form, Input } from "grab-ui"
import { MenuStoreList } from "pages/menu"

import { OutletStore } from "types/models/menu"
import { useIsMartMenuGroup } from "pages/menu-groups/useIsMartMenuGroup"
import { useTranslationsMap } from "common/utils/i18n"
import { EventAction, EventCategory } from "constants/tracking"
import { TroyWebTracker } from "utils/tracking"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import useEnableMenuV2PartialMigrate from "hooks/useEnableMenuV2PartialMigrate"
import { selectStoresStyle } from "../style"

export interface SelectStoresProps {
  menuGroupName: string
  menuGroupNameError?: string
  selectedStoreIDs: string[]
  onChangeMenuGroupName: (menuGroupName: string) => void
  onChangeSelectedStoreIDs: (selectedStoreIDs: string[]) => void
  searchInputRef: RefObject<TInput>
}

export const SelectStores: FC<SelectStoresProps> = ({
  menuGroupName,
  menuGroupNameError,
  selectedStoreIDs,
  onChangeSelectedStoreIDs,
  onChangeMenuGroupName,
  searchInputRef,
}: SelectStoresProps) => {
  const isMartMenuGroup = useIsMartMenuGroup()

  const { merchantGroupID, outlets } = useSelector(
    (state: AppState) => state.menu,
  )

  const linkStoreMaxLimit = getGrabXValueFromLocalStorage<number>(
    MerchantPortalFeatureFlag.linkStoreMaxLimit,
  )

  const handleChangeStoreIDs = (newSelectedStoreIDs: string[]) => {
    if (
      newSelectedStoreIDs.length > linkStoreMaxLimit &&
      selectedStoreIDs.length < linkStoreMaxLimit
    ) {
      onChangeSelectedStoreIDs(newSelectedStoreIDs.slice(0, linkStoreMaxLimit))
      return
    }
    onChangeSelectedStoreIDs(newSelectedStoreIDs)
  }

  const enableMenuV2Partial = useEnableMenuV2PartialMigrate(merchantGroupID)

  const getIsDisabledRow = (outlet: OutletStore) => {
    return enableMenuV2Partial && !outlet.menuGroupV2ID
  }

  const getRowClassName = (record: OutletStore) => {
    if (getIsDisabledRow(record)) return "row-disabled"
    return ""
  }

  const handleGetCheckboxProps = (outlet: OutletStore) => ({
    disabled: getIsDisabledRow(outlet),
  })

  useEffect(() => {
    if (outlets?.length) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuGroupCreationSelectStores,
        action: EventAction.pageView,
        labels: {
          MERCHANT_GROUP_ID: merchantGroupID,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outlets])

  // update selectedStoreIDs on row click
  const handleRowClick = (store: OutletStore) => {
    if (getIsDisabledRow(store)) return
    const newSelectedStoreIDs = [...selectedStoreIDs]
    if (newSelectedStoreIDs.indexOf(store.merchantID) >= 0) {
      newSelectedStoreIDs.splice(
        newSelectedStoreIDs.indexOf(store.merchantID),
        1,
      )
    } else {
      newSelectedStoreIDs.push(store.merchantID)
    }
    onChangeSelectedStoreIDs(newSelectedStoreIDs)
  }

  const textAsBizType = isMartMenuGroup ? "forCatalogue" : "forMenu"

  const text = useTranslationsMap({
    inputLable: `menu.menuGroup.create.${textAsBizType}.inputMenuName`,
    inputPlaceholder: `menu.menuGroup.create.${textAsBizType}.inputMenuNamePlaceholder`,
    selectStoreCommand: `menu.menuGroup.create.${textAsBizType}.selectStore`,
  })

  return (
    <div css={selectStoresStyle} data-testid="select-stores">
      <Form.Item
        className="input-name"
        label={<h2 className="page-subtitle">{text.inputLable} *</h2>}
        validateStatus={menuGroupNameError ? "error" : undefined}
        help={menuGroupNameError}
      >
        <Input
          placeholder={text.inputPlaceholder}
          data-testid="input-name"
          autoComplete="off"
          autoFocus
          value={menuGroupName}
          onChange={e => {
            if (onChangeMenuGroupName) {
              onChangeMenuGroupName(e.target.value)
            }
          }}
          ref={searchInputRef}
        />
      </Form.Item>
      <MenuStoreList
        title={`${text.selectStoreCommand} *`}
        rowSelection={{
          type: "checkbox",
          onChange: handleChangeStoreIDs,
          selectedRowKeys: selectedStoreIDs,
          preserveSelectedRowKeys: true,
          getCheckboxProps: handleGetCheckboxProps,
        }}
        onRowClickOverride={handleRowClick}
        eventCategory={EventCategory.menuGroupCreationSelectStores}
        eventLabels={{
          MERCHANT_GROUP_ID: merchantGroupID,
        }}
        rowClassName={getRowClassName}
        disableMenuV1={enableMenuV2Partial}
      />
    </div>
  )
}
